import {
    EnumButtonName,
    EnumDialogName,
    EnumFeature,
    EnumScreenName,
    EventProperties,
} from '@calm/evexia-analytics';
import { DS_BREAKPOINT, DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import { DialogCloseButton } from '@rhg/components/dialog-close-button';
import { MaxColumnsContainer } from '@rhg/components/ui/layout';
import {
    Box,
    Button,
    ButtonProps,
    Dialog,
    SxProps,
    Theme,
    Typography,
    TypographyProps,
    useMediaQuery,
    useTheme,
} from '@rhg/libs/material';
import { CSSProperties, ReactElement, useId } from 'react';
import { DialogProps } from '../overrides/dialog';
import { responsivePaddingDefault, sxAppend } from '../sx';
import { SimpleDialogTitle } from './title';
import {
    BodyTextAlign,
    DialogButton,
    IconColor,
    RawDialogButton,
} from './types';

const styleDialogContainer = {
    height: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
};
const styleDialogPaper = {
    backgroundColor: 'background.paper',
    backgroundImage: 'none',
};
const styleBottomActions = {
    py: 1,
    display: 'flex',
    justifyContent: 'center',
    px: {
        sm: DS_SPACING_UNITS.SIDE_SMALL,
        md: DS_SPACING_UNITS.SIDE_MEDIUM,
        lg: DS_SPACING_UNITS.SIDE_LARGE,
    },
};
const styleBottomActionsLine = {
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? 'background.paper'
            : 'background.primary',
    borderTop: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? `1px solid ${theme.palette.divider}`
            : 'none',
};
const styleButtonsContainer = {
    gap: 4,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: { xs: 'column-reverse', sm: 'column-reverse', md: 'row' },
};
const styleCloseButtonContainer = {
    position: 'absolute',
    top: 0,
    p: DS_SPACING_UNITS.SIDE_SMALL,
};

const styleCloseButtonContainerFullScreen = {
    p: DS_SPACING_UNITS.SIDE_LARGE,
};

const styleCloseButtonContainerLeft = {
    left: 0,
    right: 'unset',
};
const styleCloseButtonContainerRight = {
    right: 0,
};

export const styleSimpleDialogButton = {
    width: '100%',
    maxWidth: { xs: '100%', sm: '100%', md: DS_BREAKPOINT.MEDIUM },
    my: 2,
};
const styleContentContainer = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflowY: 'auto',
    pt: 10,
    px: responsivePaddingDefault,
};

const styleContentContainerFullScreen = {
    pt: 20,
};

const styleContentContainerGradient = {
    background: (theme: Theme) => theme.palette.gradient.backgroundPrimary,
};
const styleDialogContent = {
    pb: 3.5,
    color: 'text.primary',
    flexGrow: 1,
};
const styleDialogTitle = {
    mb: 4,
};
const styleDialogText = {
    color: 'text.primary',
};
const styleIconContainer = {
    borderRadius: '50%',
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'background.paper' : 'mask.black.20p',
    color: 'primary.main',
    width: '40px',
    height: '40px',
    p: 1,
    mb: 4,
    '& > svg': {
        width: '32px',
        height: '32px',
    },
};
const styleIconContainerPrimary = {
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'primary.light' : 'mask.black.20p',
    color: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'primary.main' : 'primary.dark',
};
const styleIconContainerSecondary = {
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'secondary.light' : 'mask.black.20p',
    color: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'secondary.main' : 'secondary.dark',
};
const styleIconContainerWarning = {
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'warning.light' : 'warning.dark',
    color: 'warning.main',
};
const styleIconContainerInfo = {
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'info.light' : 'info.dark',
    color: 'info.main',
};
const styleIconContainerFullScreen = {
    width: { xs: '64px', sm: '64px', md: '96px' },
    height: { xs: '64px', sm: '64px', md: '96px' },
    p: { xs: '6px', sm: '6px', md: '18px' },
    '& > svg': {
        width: { xs: '52px', sm: '52px', md: '60px' },
        height: { xs: '52px', sm: '52px', md: '60px' },
    },
};
const styleNoIconPadding = {
    p: 0,
};
function renderButton({
    title,
    isVisible,
    rawComponent,
    ...otherProps
}: DialogButton | RawDialogButton) {
    if (!isVisible || isVisible()) {
        if (rawComponent) {
            return rawComponent;
        } else {
            const buttonProps = otherProps as ButtonProps;
            return (
                <Button
                    key={title}
                    color="primary"
                    size="large"
                    variant="contained"
                    {...buttonProps}
                    sx={sxAppend(styleSimpleDialogButton, buttonProps.sx)}
                >
                    {title}
                </Button>
            );
        }
    } else {
        return null;
    }
}

export type SimpleDialogProps = {
    bodyText?: string;
    bodyTextAlign?: BodyTextAlign;
    content?: ReactElement | ReactElement[] | null;
    contentWrapperStyle?: SxProps<Theme>;
    buttons?: (DialogButton | RawDialogButton)[];
    disableBackdropClick?: boolean;
    disableMobileFullscreen?: boolean;
    hideCloseButton?: boolean;
    isOpen: boolean;
    closeButtonPosition?: 'left' | 'right';
    hasGradientBackground?: boolean;
    noIconPadding?: boolean;
    dialogMaxWidth?: CSSProperties['maxWidth'];
    trackDialog: EnumDialogName;
    trackScreen?: EnumScreenName;
    trackFeature?: EnumFeature;
    trackProps?: EventProperties;
    onClose: () => void;
    title?: string;
    titleVariant?: TypographyProps['variant'];
    titleTextAlign?: TypographyProps['textAlign'];
    titleColor?: TypographyProps['color'];
    titleWrapperStyle?: SxProps<Theme>;
    titleStyle?: SxProps<Theme>;
    bottomActionsWrapperStyle?: SxProps<Theme>;
    buttonsContainerStyle?: SxProps<Theme>;
    closeButtonWrapperStyle?: SxProps<Theme>;
    contentContainerStyle?: SxProps<Theme>;
    trackCloseButton?: EnumButtonName;
    iconContainerOverride?: ReactElement;
} & Partial<Omit<DialogProps, 'content'>> &
    (
        | {
              icon?: ReactElement | null;
              inheritIconSize?: boolean;
              iconColor?: IconColor;
              iconContainerStyle?: SxProps<Theme>;
          }
        | {
              icon?: never;
              inheritIconSize?: never;
              iconColor?: never;
              iconContainerStyle?: never;
          }
    );

/**
 * Before using this component directly, see if you can use the `RegistrationSimpleDialog` component instead as it provides styling that aligns more directly with our design system.
 */
export const SimpleDialog = ({
    bodyText,
    bodyTextAlign = 'left',
    content,
    contentWrapperStyle = undefined,
    buttons = [],
    'aria-labelledby': labelledBy,
    icon,
    iconColor = 'transparent',
    hideCloseButton = false,
    disableBackdropClick = false,
    disableMobileFullscreen = false,
    hasGradientBackground = false,
    inheritIconSize = false,
    noIconPadding = false,
    dialogMaxWidth = undefined,
    isOpen,
    closeButtonPosition = 'right',
    onClose,
    title,
    titleVariant,
    titleTextAlign = 'center',
    titleColor,
    titleWrapperStyle = undefined,
    titleStyle,
    trackDialog,
    trackScreen,
    trackFeature,
    trackProps,
    trackCloseButton = 'Close',
    bottomActionsWrapperStyle,
    buttonsContainerStyle,
    closeButtonWrapperStyle,
    contentContainerStyle,
    iconContainerStyle,
    iconContainerOverride,
    ...dialogProps
}: SimpleDialogProps) => {
    const id = useId();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isFullScreen =
        (!disableMobileFullscreen && isMobile) || dialogProps.fullScreen;

    const showCloseButton = !hideCloseButton;

    const handleOnClose = (
        _: any,
        reason: 'backdropClick' | 'escapeKeyDown'
    ) => {
        if (disableBackdropClick && reason === 'backdropClick') {
            return;
        }
        onClose();
    };

    return (
        <Dialog
            aria-labelledby={labelledBy ?? id}
            {...dialogProps}
            PaperProps={{
                ...dialogProps.PaperProps,
                sx: sxAppend(
                    {
                        maxWidth: dialogMaxWidth ?? 'none',
                    },
                    styleDialogPaper,
                    dialogProps.PaperProps?.sx
                ),
            }}
            fullScreen={isFullScreen}
            trackDialog={trackDialog}
            trackScreen={trackScreen}
            trackFeature={trackFeature}
            trackProps={trackProps}
            onClose={handleOnClose}
            open={isOpen}
        >
            <Box sx={sxAppend(isFullScreen && styleDialogContainer)}>
                {showCloseButton && (
                    <Box
                        sx={sxAppend(
                            styleCloseButtonContainer,
                            closeButtonPosition === 'left' &&
                                styleCloseButtonContainerLeft,
                            closeButtonPosition === 'right' &&
                                styleCloseButtonContainerRight,
                            isFullScreen && styleCloseButtonContainerFullScreen,
                            closeButtonWrapperStyle
                        )}
                    >
                        <DialogCloseButton
                            onClick={onClose}
                            trackButton={trackCloseButton}
                            trackProps={trackProps}
                            isFullScreen={isFullScreen}
                        />
                    </Box>
                )}
                <Box
                    sx={sxAppend(
                        styleContentContainer,
                        hasGradientBackground && styleContentContainerGradient,
                        isFullScreen && styleContentContainerFullScreen,
                        contentContainerStyle
                    )}
                >
                    {iconContainerOverride
                        ? iconContainerOverride
                        : icon && (
                              <Box
                                  sx={sxAppend(
                                      !inheritIconSize && styleIconContainer,
                                      !inheritIconSize &&
                                          isFullScreen &&
                                          styleIconContainerFullScreen,
                                      iconColor === 'primary' &&
                                          styleIconContainerPrimary,
                                      iconColor === 'secondary' &&
                                          styleIconContainerSecondary,
                                      iconColor === 'warning' &&
                                          styleIconContainerWarning,
                                      iconColor === 'info' &&
                                          styleIconContainerInfo,
                                      noIconPadding && styleNoIconPadding,
                                      iconContainerStyle
                                  )}
                              >
                                  {icon}
                              </Box>
                          )}
                    {title && (
                        <>
                            <MaxColumnsContainer
                                columns={6}
                                sx={sxAppend(
                                    styleDialogTitle,
                                    titleWrapperStyle
                                )}
                                id={id}
                            >
                                {title && (
                                    <SimpleDialogTitle
                                        title={title}
                                        titleVariant={titleVariant}
                                        titleTextAlign={titleTextAlign}
                                        titleColor={titleColor}
                                        sx={sxAppend(
                                            styleDialogTitle,
                                            titleStyle
                                        )}
                                    />
                                )}
                            </MaxColumnsContainer>
                        </>
                    )}
                    {(bodyText || content) && (
                        <MaxColumnsContainer
                            columns={6}
                            sx={sxAppend(
                                styleDialogContent,
                                contentWrapperStyle
                            )}
                        >
                            {bodyText &&
                                bodyText.split('\n').map((line, index) => (
                                    <Typography
                                        key={line}
                                        variant="body1"
                                        component="div"
                                        textAlign={bodyTextAlign}
                                        color="text.body"
                                        sx={{
                                            ...styleDialogText,
                                            mt: index === 0 ? 0 : 2,
                                        }}
                                    >
                                        {line}
                                    </Typography>
                                ))}
                            {content}
                        </MaxColumnsContainer>
                    )}
                </Box>
                {/* Only show the dialog buttons if at least one is visible */}
                {!!buttons.find((x) => !x.isVisible || x.isVisible()) && (
                    <Box
                        sx={sxAppend(
                            styleBottomActions,
                            isFullScreen && styleBottomActionsLine,
                            bottomActionsWrapperStyle
                        )}
                    >
                        <MaxColumnsContainer
                            columns={6}
                            sx={sxAppend(
                                styleButtonsContainer,
                                buttonsContainerStyle
                            )}
                        >
                            {buttons.map(renderButton)}
                        </MaxColumnsContainer>
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};

export default SimpleDialog;
